import { useEffect, useState } from 'react';
import useSalesBotStore from '@/stores/SalesBotStore';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveAutopilotSettings } from '@/hooks/sales-bot';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';

const AutopilotSettings = () => {
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [localSelectedBookingService, setLocalSelectedBookingService] =
    useState('');
  const [localEnableCallBooking, setLocalEnableCallBooking] = useState(false);
  const [localWaitSecondsMin, setLocalWaitSecondsMin] = useState('61');
  const [localWaitSecondsMax, setLocalWaitSecondsMax] = useState('61');
  const [localWaitSecondsMultipleMin, setLocalWaitSecondsMultipleMin] =
    useState('1');
  const [localWaitSecondsMultipleMax, setLocalWaitSecondsMultipleMax] =
    useState('1');
  const [localTimezone, setLocalTimezone] = useState('');
  const [localBookingDurationMinutes, setLocalBookingDurationMinutes] =
    useState('30');
  const [localTimeWindowEnabled, setLocalTimeWindowEnabled] = useState(false);
  const [localTimeWindowStartTime, setLocalTimeWindowStartTime] = useState('');
  const [localTimeWindowEndTime, setLocalTimeWindowEndTime] = useState('');

  const {
    bookingServices,
    selectedBookingService,
    enableCallBooking,
    waitSecondsRangeToRespondContact,
    waitSecondsRangeForMultipleMessages,
    availableTimezones,
    timezone,
    bookingDurationMinutes,
    timeWindow,
    setSelectedBookingService,
    setEnableCallBooking,
    setWaitSecondsRangeToRespondContact,
    setWaitSecondsRangeForMultipleMessages,
    setTimezone,
    setBookingDurationMinutes,
    setTimeWindow,
  } = useSalesBotStore(
    useShallow((state) => ({
      bookingServices: state.bookingServices,
      selectedBookingService: state.selectedBookingService,
      enableCallBooking: state.enableCallBooking,
      waitSecondsRangeToRespondContact: state.waitSecondsRangeToRespondContact,
      waitSecondsRangeForMultipleMessages:
        state.waitSecondsRangeForMultipleMessages,
      availableTimezones: state.availableTimezones,
      timezone: state.timezone,
      bookingDurationMinutes: state.bookingDurationMinutes,
      timeWindow: state.timeWindow,
      setSelectedBookingService: state.setSelectedBookingService,
      setEnableCallBooking: state.setEnableCallBooking,
      setWaitSecondsRangeToRespondContact:
        state.setWaitSecondsRangeToRespondContact,
      setWaitSecondsRangeForMultipleMessages:
        state.setWaitSecondsRangeForMultipleMessages,
      setTimezone: state.setTimezone,
      setBookingDurationMinutes: state.setBookingDurationMinutes,
      setTimeWindow: state.setTimeWindow,
    }))
  );

  // Initialize local state from store when it's available
  useEffect(() => {
    setLocalSelectedBookingService(selectedBookingService || '');
    setLocalEnableCallBooking(enableCallBooking);
  }, [selectedBookingService, enableCallBooking]);

  // Initialize wait seconds range from store
  useEffect(() => {
    if (waitSecondsRangeToRespondContact) {
      setLocalWaitSecondsMin(String(waitSecondsRangeToRespondContact.min || 0));
      setLocalWaitSecondsMax(String(waitSecondsRangeToRespondContact.max || 0));
    }
  }, [waitSecondsRangeToRespondContact]);

  // Initialize wait seconds range for multiple messages from store
  useEffect(() => {
    if (waitSecondsRangeForMultipleMessages) {
      setLocalWaitSecondsMultipleMin(
        String(waitSecondsRangeForMultipleMessages.min || 0)
      );
      setLocalWaitSecondsMultipleMax(
        String(waitSecondsRangeForMultipleMessages.max || 0)
      );
    }
  }, [waitSecondsRangeForMultipleMessages]);

  // Initialize timezone from store
  useEffect(() => {
    if (timezone) {
      setLocalTimezone(timezone);
    }
  }, [timezone]);

  // Initialize booking duration from store
  useEffect(() => {
    if (bookingDurationMinutes) {
      setLocalBookingDurationMinutes(String(bookingDurationMinutes));
    }
  }, [bookingDurationMinutes]);

  // Initialize time window from store
  useEffect(() => {
    if (timeWindow) {
      setLocalTimeWindowEnabled(timeWindow.enabled || false);
      setLocalTimeWindowStartTime(timeWindow.start_time || '');
      setLocalTimeWindowEndTime(timeWindow.end_time || '');
    }
  }, [timeWindow]);

  const hasChanges =
    localSelectedBookingService !== (selectedBookingService || '') ||
    localEnableCallBooking !== enableCallBooking ||
    localWaitSecondsMin !==
    String(waitSecondsRangeToRespondContact?.min || 0) ||
    localWaitSecondsMax !==
    String(waitSecondsRangeToRespondContact?.max || 0) ||
    localWaitSecondsMultipleMin !==
    String(waitSecondsRangeForMultipleMessages?.min || 0) ||
    localWaitSecondsMultipleMax !==
    String(waitSecondsRangeForMultipleMessages?.max || 0) ||
    localTimezone !== (timezone || '') ||
    localBookingDurationMinutes !== String(bookingDurationMinutes || '') ||
    localTimeWindowEnabled !== (timeWindow?.enabled || false) ||
    localTimeWindowStartTime !== (timeWindow?.start_time || '') ||
    localTimeWindowEndTime !== (timeWindow?.end_time || '');

  const handleSaveSettings = async () => {
    try {
      // Validate wait seconds range before saving
      const minValue = parseInt(localWaitSecondsMin, 10);
      const maxValue = parseInt(localWaitSecondsMax, 10);

      if (isNaN(minValue) || isNaN(maxValue)) {
        toast.error('Please enter valid numbers for wait seconds range');
        return;
      }

      if (minValue < 61) {
        toast.error('Minimum wait seconds must be at least 61 seconds');
        return;
      }

      if (maxValue < minValue) {
        toast.error(
          'Maximum wait seconds must be greater than or equal to minimum'
        );
        return;
      }

      // Validate wait seconds range for multiple messages
      const multipleMinValue = parseInt(localWaitSecondsMultipleMin, 10);
      const multipleMaxValue = parseInt(localWaitSecondsMultipleMax, 10);

      if (isNaN(multipleMinValue) || isNaN(multipleMaxValue)) {
        toast.error(
          'Please enter valid numbers for wait seconds range for multiple messages'
        );
        return;
      }

      if (multipleMinValue < 1) {
        toast.error(
          'Minimum wait seconds for multiple messages must be at least 1 second'
        );
        return;
      }

      if (multipleMaxValue > 5) {
        toast.error(
          'Maximum wait seconds for multiple messages must be at most 5 seconds'
        );
        return;
      }

      if (multipleMaxValue < multipleMinValue) {
        toast.error(
          'Maximum wait seconds for multiple messages must be greater than or equal to minimum'
        );
        return;
      }

      // Validate booking duration minutes
      const bookingDurationValue = parseInt(localBookingDurationMinutes, 10);
      if (isNaN(bookingDurationValue)) {
        toast.error('Please enter a valid number for booking duration');
        return;
      }

      if (bookingDurationValue < 15 || bookingDurationValue > 240) {
        toast.error('Booking duration must be between 15 and 240 minutes');
        return;
      }

      // Validate time window
      if (localTimeWindowEnabled) {
        if (!localTimezone) {
          toast.error(
            'Please select a timezone before enabling the time window'
          );
          return;
        }

        if (!localTimeWindowStartTime || !localTimeWindowEndTime) {
          toast.error(
            'Please set both start and end times for the time window'
          );
          return;
        }

        // Validate time format (HH:mm)
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(localTimeWindowStartTime)) {
          toast.error('Please enter a valid start time in HH:mm format');
          return;
        }
        if (!timeRegex.test(localTimeWindowEndTime)) {
          toast.error('Please enter a valid end time in HH:mm format');
          return;
        }
      }

      setIsSavingSettings(true);

      const waitSecondsRange = {
        min: minValue,
        max: maxValue,
      };

      const waitSecondsRangeForMultiple = {
        min: multipleMinValue,
        max: multipleMaxValue,
      };

      const settingsPayload = {
        enable_call_booking: localEnableCallBooking,
        wait_seconds_range_to_respond_contact: waitSecondsRange,
        wait_seconds_range_for_multiple_messages: waitSecondsRangeForMultiple,
      };

      // Only include booking_service if selected
      if (localSelectedBookingService) {
        settingsPayload.booking_service = localSelectedBookingService;
      }

      // Add timezone if selected
      if (localTimezone) {
        settingsPayload.timezone = localTimezone;
      }

      // Add booking duration if provided
      if (bookingDurationValue) {
        settingsPayload.booking_duration_minutes = bookingDurationValue;
      }

      // Add time window settings
      const timeWindowPayload = {
        enabled: localTimeWindowEnabled,
      };
      if (localTimeWindowStartTime) {
        timeWindowPayload.start_time = localTimeWindowStartTime;
      }
      if (localTimeWindowEndTime) {
        timeWindowPayload.end_time = localTimeWindowEndTime;
      }
      settingsPayload.time_window = timeWindowPayload;

      await toast.promise(saveAutopilotSettings(settingsPayload), {
        loading: 'Saving settings...',
        success: () => {
          setSelectedBookingService(localSelectedBookingService);
          setEnableCallBooking(localEnableCallBooking);
          // Store the values as-is
          setWaitSecondsRangeToRespondContact({
            min: waitSecondsRange.min,
            max: waitSecondsRange.max,
          });
          setWaitSecondsRangeForMultipleMessages({
            min: waitSecondsRangeForMultiple.min,
            max: waitSecondsRangeForMultiple.max,
          });
          setTimezone(localTimezone);
          setBookingDurationMinutes(bookingDurationValue);
          setTimeWindow(timeWindowPayload);
          setIsSavingSettings(false);
          return 'Settings saved successfully';
        },
        error: (error) => {
          setIsSavingSettings(false);
          return error.response?.data?.message || 'Failed to save settings';
        },
      });
    } catch (error) {
      toast.error('Failed to save settings');
      setIsSavingSettings(false);
    }
  };

  const bookingServicesOptions = {
    ghl: 'Go High Level',
    calendly: 'Calendly',
  };

  return (
    <div className='flex h-full flex-col p-6'>
      <div className='space-y-6'>
        <div className='flex max-w-[400px] items-center justify-between'>
          <div className='flex-1'>
            <p className='text-sm text-muted-foreground'>
              Allow the autopilot to use the booking service to book calls with prospects.
            </p>
          </div>
          <Checkbox
            id='enable-call-booking'
            checked={localEnableCallBooking}
            onCheckedChange={setLocalEnableCallBooking}
            className='h-6 w-6'
          />
        </div>

        <div className='max-w-[400px] space-y-2'>
          <Label htmlFor='booking-service'>Select Booking Service</Label>
          <Select
            value={localSelectedBookingService}
            onValueChange={setLocalSelectedBookingService}
          >
            <SelectTrigger id='booking-service'>
              <SelectValue placeholder='Select a booking service' />
            </SelectTrigger>
            <SelectContent>
              {!bookingServices || bookingServices.length === 0 ? (
                <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                  No booking services available
                </div>
              ) : (
                bookingServices.map((service) => (
                  <SelectItem key={service.name} value={service.name}>
                    {bookingServicesOptions[service.name]}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className='max-w-[400px] space-y-2'>
          <Label htmlFor='booking-duration-minutes'>
            Booking Duration (minutes)
          </Label>
          <Input
            id='booking-duration-minutes'
            type='number'
            min='15'
            max='240'
            value={localBookingDurationMinutes}
            onChange={(e) => {
              setLocalBookingDurationMinutes(e.target.value);
            }}
          />
          <p className='text-xs text-muted-foreground'>
            Note: The booking duration must be between 15 and 240 minutes.
          </p>
        </div>

        <Separator />

        <div className='max-w-[400px] space-y-2'>
          <Label htmlFor='timezone'>Timezone</Label>
          <Select value={localTimezone} onValueChange={setLocalTimezone}>
            <SelectTrigger id='timezone'>
              <SelectValue placeholder='Select a timezone' />
            </SelectTrigger>
            <SelectContent>
              {!availableTimezones || availableTimezones.length === 0 ? (
                <div className='px-2 py-1.5 text-sm text-muted-foreground'>
                  No timezones available
                </div>
              ) : (
                availableTimezones.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <div className='space-y-4'>
          <div className='flex max-w-[400px] items-center justify-between'>
            <div className='flex-1'>
              <Label htmlFor='enable-time-window'>Enable Time Window</Label>
              <p className='text-sm text-muted-foreground'>
                Restrict autopilot responses to specific hours of the day.
              </p>
            </div>
            <Checkbox
              id='enable-time-window'
              checked={localTimeWindowEnabled}
              onCheckedChange={setLocalTimeWindowEnabled}
              className='h-6 w-6'
            />
          </div>

          {localTimeWindowEnabled && (
            <div className='max-w-[600px] space-y-4'>
              {!localTimezone && (
                <p className='text-sm text-destructive'>
                  Please select a timezone above to enable the time window.
                </p>
              )}
              <div className='flex items-center gap-4'>
                <div className='flex-1 space-y-2'>
                  <Label htmlFor='time-window-start' className='text-xs'>
                    Start Time
                  </Label>
                  <Input
                    id='time-window-start'
                    type='time'
                    className='[&::-webkit-calendar-picker-indicator]:dark:invert'
                    value={localTimeWindowStartTime}
                    onChange={(e) => setLocalTimeWindowStartTime(e.target.value)}
                  />
                </div>
                <div className='flex-1 space-y-2'>
                  <Label htmlFor='time-window-end' className='text-xs'>
                    End Time
                  </Label>
                  <Input
                    id='time-window-end'
                    type='time'
                    className='[&::-webkit-calendar-picker-indicator]:dark:invert'
                    value={localTimeWindowEndTime}
                    onChange={(e) => setLocalTimeWindowEndTime(e.target.value)}
                  />
                </div>
              </div>
              <p className='text-xs text-muted-foreground'>
                The autopilot will only respond to contacts and send follow-ups
                during the specified time window in your selected timezone.
              </p>
            </div>
          )}
        </div>

        <Separator />

        <div className=' space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='wait-seconds-range'>
              Wait Seconds Range to Respond Contact
            </Label>
            <p className='text-sm text-muted-foreground'>
              Set the min and max wait time (in seconds) before the autopilot
              responds to a contact message.
            </p>
            <div className='flex max-w-[600px] items-center gap-4'>
              <div className='flex-1 space-y-2'>
                <Label htmlFor='wait-seconds-min' className='text-xs'>
                  Minimum (seconds)
                </Label>
                <Input
                  id='wait-seconds-min'
                  type='number'
                  value={localWaitSecondsMin}
                  onChange={(e) => {
                    setLocalWaitSecondsMin(e.target.value);
                  }}
                />
              </div>
              <div className='flex-1 space-y-2'>
                <Label htmlFor='wait-seconds-max' className='text-xs'>
                  Maximum (seconds)
                </Label>
                <Input
                  id='wait-seconds-max'
                  type='number'
                  value={localWaitSecondsMax}
                  onChange={(e) => {
                    setLocalWaitSecondsMax(e.target.value);
                  }}
                />
              </div>
            </div>
            <p className='text-xs text-muted-foreground'>
              Note: The minimum value for both fields is 61 seconds.
            </p>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='wait-seconds-range-multiple'>
              Wait Seconds Range for Multiple Messages
            </Label>
            <p className='text-sm text-muted-foreground'>
              Set the min and max wait time (in seconds) before the autopilot
              responds when there are multiple messages.
            </p>
            <div className='flex max-w-[600px] items-center gap-4'>
              <div className='flex-1 space-y-2'>
                <Label htmlFor='wait-seconds-multiple-min' className='text-xs'>
                  Minimum (seconds)
                </Label>
                <Input
                  id='wait-seconds-multiple-min'
                  type='number'
                  min='1'
                  max='5'
                  value={localWaitSecondsMultipleMin}
                  onChange={(e) => {
                    setLocalWaitSecondsMultipleMin(e.target.value);
                  }}
                />
              </div>
              <div className='flex-1 space-y-2'>
                <Label htmlFor='wait-seconds-multiple-max' className='text-xs'>
                  Maximum (seconds)
                </Label>
                <Input
                  id='wait-seconds-multiple-max'
                  type='number'
                  min='1'
                  max='5'
                  value={localWaitSecondsMultipleMax}
                  onChange={(e) => {
                    setLocalWaitSecondsMultipleMax(e.target.value);
                  }}
                />
              </div>
            </div>
            <p className='text-xs text-muted-foreground'>
              Note: The minimum value is 1 second and maximum value is 5
              seconds.
            </p>
          </div>
        </div>

        <Button
          onClick={handleSaveSettings}
          disabled={isSavingSettings || !hasChanges}
          className='w-64'
        >
          {isSavingSettings ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </Button>
      </div>
    </div>
  );
};

export default AutopilotSettings;
